import ReactMarkdown from "react-markdown";

import { stripFirmwareReleaseNoteBanners } from "@integrator-src/engineering/firmware-release-note-text";
import { cn } from "@/lib/utils";

export function FirmwareReleaseNoteMarkdown({
  text,
  compact = false,
  className,
}: {
  text: string;
  compact?: boolean;
  className?: string;
}): JSX.Element {
  const trimmed = stripFirmwareReleaseNoteBanners(text);
  if (trimmed.length === 0) {
    return <p className="text-sm text-slate-500">No changelog text on this release note.</p>;
  }

  return (
    <div className={cn(compact ? "text-xs" : "text-sm", className)}>
      <ReactMarkdown
        components={{
          a: ({ children }) => <span className="text-slate-800">{children}</span>,
          h1: ({ children }) => (
            <p className={cn("font-semibold text-slate-950 first:mt-0", compact ? "mb-1.5 mt-2" : "mb-2 mt-3")}>
              {children}
            </p>
          ),
          h2: ({ children }) => (
            <p className={cn("font-semibold text-slate-950", compact ? "mb-1 mt-2" : "mb-1.5 mt-3")}>{children}</p>
          ),
          h3: ({ children }) => (
            <p className={cn("font-semibold text-slate-950", compact ? "mb-1 mt-2" : "mb-1.5 mt-3")}>{children}</p>
          ),
          p: ({ children }) => (
            <p className={cn("text-slate-800", compact ? "my-1 leading-5" : "my-2 leading-6")}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul className={cn("list-disc text-slate-800", compact ? "my-1 space-y-0.5 pl-4" : "my-2 space-y-1 pl-5")}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className={cn("list-decimal text-slate-800", compact ? "my-1 space-y-0.5 pl-4" : "my-2 space-y-1 pl-5")}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => <li className={compact ? "leading-5" : "leading-6"}>{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-slate-950">{children}</strong>,
          code: ({ children }) => (
            <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-900">{children}</code>
          ),
        }}
      >
        {trimmed}
      </ReactMarkdown>
    </div>
  );
}
